import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FaSpinner,
  FaBox,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaTruck,
  FaImage,
  FaEye,
  FaEyeSlash,
  FaUserCheck,
  FaCalendarCheck,
} from "react-icons/fa";
import { getParcelById } from "../../services/parcelService";
import EleghoLogo from "../assets/Elegho-Logo.png";
import { appName, unitNumberLabel } from "../../utils/constants";

const ParcelNotice = () => {
  const { id } = useParams();
  const [parcel, setParcel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImage, setShowImage] = useState(false);

  const calculateDaysUnclaimed = (dateString) => {
    const deliveryDate = new Date(dateString);
    const today = new Date();
    const differenceMs = today - deliveryDate;
    return Math.floor(differenceMs / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  useEffect(() => {
    const fetchParcel = async () => {
      try {
        const data = await getParcelById(id);
        setParcel(data);
      } catch (err) {
        setError("Failed to load parcel details");
        console.error("Error fetching parcel:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchParcel();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center space-x-2">
          <FaSpinner className="animate-spin text-[#363062]" />
          <span className="text-[#363062]">Loading parcel details...</span>
        </div>
      </div>
    );
  }

  if (error || !parcel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-[#363062] mb-2">
            Parcel Not Found
          </h2>
          <p className="text-gray-600">
            The parcel you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src={EleghoLogo} alt="Elegho Logo" className="h-8" />
            <div className="h-6 w-px bg-gray-200"></div>
            <h1 className="text-[#363062] font-medium">
              {appName} - Parcel Notice
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Status Banner */}
          <div className="bg-[#363062] text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Parcel Status</p>
                <div className="flex items-center space-x-2">
                  <p className="font-medium">{parcel.status}</p>
                  {parcel.status === "Stored" && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        calculateDaysUnclaimed(parcel.dateTime) > 7
                          ? "bg-red-400 text-white"
                          : calculateDaysUnclaimed(parcel.dateTime) > 3
                          ? "bg-yellow-400 text-[#363062]"
                          : "bg-white/20"
                      }`}
                    >
                      {calculateDaysUnclaimed(parcel.dateTime)} days unclaimed
                    </span>
                  )}
                </div>
              </div>
              <div className="text-[#8FCD00]">
                <FaBox size={24} />
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Parcel Image Section */}
            {parcel.imageUrl && (
              <div className="mb-6">
                <button
                  onClick={() => setShowImage(!showImage)}
                  className="flex items-center space-x-2 text-sm font-medium text-[#363062] hover:text-[#8FCD00] transition-colors mb-2"
                >
                  {showImage ? (
                    <>
                      <FaEyeSlash className="text-[#363062]" />
                      <span>Hide Parcel Image</span>
                    </>
                  ) : (
                    <>
                      <FaImage className="text-[#363062]" />
                      <span>View Parcel Image</span>
                    </>
                  )}
                </button>
                {showImage && (
                  <div className="animate-fade-in">
                    <img
                      src={parcel.imageUrl}
                      alt="Parcel"
                      className="w-full h-auto rounded-lg shadow-sm"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Parcel Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <FaBox className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Parcel Number</p>
                  <p className="font-medium">{parcel.parcelNumber}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FaUser className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Recipient</p>
                  <p className="font-medium">{parcel.recipientName}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FaBuilding className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">{unitNumberLabel}</p>
                  <p className="font-medium">{parcel.unitNumber}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FaTruck className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Delivery Company</p>
                  <p className="font-medium">
                    {parcel.deliveryCompany || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Storage Location</p>
                  <p className="font-medium">
                    {parcel.storageLocation || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FaClock className="text-[#363062] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Date & Time</p>
                  <p className="font-medium">{formatDate(parcel.dateTime)}</p>
                </div>
              </div>

              {parcel.createdBy && (
                <div className="flex items-start space-x-3">
                  <FaUser className="text-[#363062] mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Logged by</p>
                    <p className="font-medium">
                      {parcel.createdByName || parcel.createdBy}
                    </p>
                  </div>
                </div>
              )}

              {parcel.status === "Claimed" && (
                <>
                  {parcel.releasedByName && (
                    <div className="flex items-start space-x-3">
                      <FaUserCheck className="text-[#363062] mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Released by</p>
                        <p className="font-medium">{parcel.releasedByName}</p>
                      </div>
                    </div>
                  )}

                  {parcel.releasedDateTime && (
                    <div className="flex items-start space-x-3">
                      <FaCalendarCheck className="text-[#363062] mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Released on</p>
                        <p className="font-medium">
                          {formatDate(parcel.releasedDateTime)}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-[#363062]/5 rounded-lg border border-[#363062]/10">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#8FCD00]"></div>
                <h3 className="text-sm font-medium text-[#363062]">
                  Collection Instructions
                </h3>
              </div>
              <p className="text-gray-700 text-sm">
                Your parcel is currently stored at{" "}
                <span className="font-medium">
                  {parcel.storageLocation || "our facility"}
                </span>
                . Please collect it at your earliest convenience. If you have
                any questions, please contact the reception or the security
                desk.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParcelNotice;
